import { Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { BellIcon, XMarkIcon } from '@heroicons/react/24/outline';

import NotificationList from '../notifications/NotificationList';
import { useNotifications } from '../../context/NotificationContext';
import { activateNotification } from '../../utils/notificationLinks';

export default function NotificationBell() {
  const navigate = useNavigate();
  const {
    items,
    unreadCount,
    loading,
    open,
    setOpen,
    nextCursor,
    fetchMine,
    markRead,
  } = useNotifications();

  const onItemActivate = (item, href) => {
    activateNotification(item, href, {
      navigate,
      markRead,
      onClose: () => setOpen(false),
    });
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative rounded-xl p-2 text-surface-500 hover:bg-surface-100 hover:text-dark-900 transition-colors"
        aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
      >
        <BellIcon className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-600 text-white text-[10px] font-bold flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      <Transition show={open} as={Fragment}>
        <Dialog onClose={() => setOpen(false)} className="relative z-[80]">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-dark-900/20" aria-hidden="true" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-200"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-150"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                    <div className="flex h-full flex-col bg-white shadow-xl border-l border-surface-200">
                      <div className="flex items-center justify-between px-4 py-4 border-b border-surface-200">
                        <Dialog.Title className="text-base font-semibold text-dark-900">
                          Notifications
                        </Dialog.Title>
                        <div className="flex items-center gap-2">
                          <Link
                            to="/inbox"
                            onClick={() => setOpen(false)}
                            className="text-xs font-medium text-brand-600 hover:underline"
                          >
                            View all
                          </Link>
                          <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="rounded-lg p-1.5 text-surface-400 hover:bg-surface-100 hover:text-dark-900"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>

                      <div className="flex-1 overflow-y-auto">
                        <NotificationList
                          items={items}
                          loading={loading}
                          compact
                          onItemActivate={onItemActivate}
                        />
                      </div>

                      {nextCursor && (
                        <div className="border-t border-surface-200 p-3">
                          <button
                            type="button"
                            disabled={loading}
                            onClick={() => fetchMine({ cursor: nextCursor, append: true })}
                            className="w-full rounded-lg border border-surface-200 py-2 text-xs font-medium text-surface-600 hover:bg-surface-50 disabled:opacity-50"
                          >
                            Load older
                          </button>
                        </div>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
